import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  KPIS_SIDEMENU_OPTION,
  QUICKSTATS_SIDEMENU_OPTION,
  SEARCHBAR_SIDEMENU_OPTION,
  TABLE_SIDEMENU_OPTION,
} from 'consts';
import {
  deepCopy,
  filterSearchBarFiltersByIds,
  FooterText,
  generateSearchbarSideMenu,
  generateTableSideMenu,
  generateWidgetsSideMenu,
  getAllAvailableProps,
  getVisibleProps,
} from 'utils';

export default function usePageSettings(
  availableResources,
  currentTemplate,
  resourceType,
  allSearchBarFilters,
  allKpisStructure,
  allTableColumns,
  allWidgetStructure,
  setCurrentTemplate,
) {
  const currentResource = availableResources?.find((res) => res?.resource_type === resourceType);

  const {
    available_fields: availableFields,
    available_filters: availableFilters,
    available_widgets: availableWidgets,
  } = currentResource ?? {};

  const { t } = useTranslation('');

  if (!currentResource) {
    return {};
  }
  // filters search bar parameters from conf.json
  const allAvaillableSearchBarFiltersProps = getAllAvailableProps(
    currentTemplate?.searchbar?.filters, availableFilters,
  );
  const allAvailableSearchBarFiltersIds = allAvaillableSearchBarFiltersProps.map(({ id }) => id);
  const visibleSearchBarFiltersProps = getVisibleProps(
    currentTemplate?.searchbar?.filters, availableFilters,
  );
  const visibleSearchBarFiltersIds = visibleSearchBarFiltersProps.map(({ id }) => id);

  // filters search bar parameters from fullConfiguration.jsx
  const allFullConfSearchBarFilters = allSearchBarFilters;
  const fullConfAvailableSearchBarFilters = filterSearchBarFiltersByIds(
    allFullConfSearchBarFilters, allAvailableSearchBarFiltersIds,
  );
  const fullConfVisibleSearchBarFilters = filterSearchBarFiltersByIds(allFullConfSearchBarFilters, visibleSearchBarFiltersIds);

  // filters kpis parameters from conf.json
  const allAvailableKpisProps = getAllAvailableProps(
    currentTemplate?.kpis?.kpis || [], availableWidgets,
  );
  const allAvailableKpisIds = allAvailableKpisProps?.map(({ id }) => id);
  const visibleKpisProps = getVisibleProps(
    currentTemplate?.kpis?.kpis || [], availableWidgets,
  );
  const visibleKpis = visibleKpisProps?.map(({ id }) => id);

  // filters kpis parameters from fullConfiguration.jsx
  const allFullConfKpis = allKpisStructure;
  const fullConfAvailableKpis = allFullConfKpis.filter(
    (val) => allAvailableKpisIds?.includes(val.id),
  ).filter(Boolean);

  const fullConfVisibleKpis = fullConfAvailableKpis.filter(
    (val) => visibleKpis.includes(val.id),
  ).filter(Boolean);

  // filters widgets parameters from conf.json
  const allAvailableWidgetsProps = getAllAvailableProps(
    currentTemplate?.quickStats?.widgets || [], availableWidgets,
  );
  const allAvailableWidgetsIds = allAvailableWidgetsProps?.map(({ id }) => id);
  const visibleWidgetsProps = getVisibleProps(
    currentTemplate?.quickStats?.widgets || [], availableWidgets,
  );
  const visibleWidgets = visibleWidgetsProps?.map(({ id }) => id);

  // filters widgets parameters from fullConfiguration.jsx
  const allFullConfWidgets = allWidgetStructure;
  const fullConfAvailableWidgets = allFullConfWidgets.filter(
    (val) => allAvailableWidgetsIds?.includes(val.id),
  ).filter(Boolean);

  const fullConfVisibleWidgets = fullConfAvailableWidgets.filter(
    (val) => visibleWidgets.includes(val.id),
  ).filter(Boolean);

  // filters table parameters from conf.json
  const tableFields = availableFields;
  const allAvaillableTableColumnsProps = getAllAvailableProps(
    currentTemplate?.table?.columns, tableFields,
  );
  const allAvailableTableColumnsIds = allAvaillableTableColumnsProps.map(({ id }) => id);
  const visibleTableColumnsProps = getVisibleProps(
    currentTemplate?.table?.columns, tableFields,
  );
  const visibleTableColumnsIds = visibleTableColumnsProps.map(({ id }) => id);

  // filters table parameters from fullConfiguration.jsx
  const allFullConfTableColumns = allTableColumns;
  const fullConfAvailableTableColumns = allFullConfTableColumns.filter(
    (val) => allAvailableTableColumnsIds.includes(val.id),
  ).filter(Boolean);

  const fullConfVisibleTableColumns = fullConfAvailableTableColumns.filter(
    (val) => visibleTableColumnsIds.includes(val.id),
  ).filter(Boolean);

  const doINeedToPutFooter = (val, index) => (index === 0 && !val?.footer
    && fullConfAvailableTableColumns.some((fl) => fl?.footer));

  // function that returns the id of the columns that i need to show into MainTable
  const visibleTableColumns = () => fullConfVisibleTableColumns.map((val, index) => (doINeedToPutFooter(val, index) ? {
    ...val,
    footer: () => <FooterText>{t('total')}</FooterText>,
  } : val));

  const saveNewTemplateToggles = (newData) => {
    const newTemplate = deepCopy(currentTemplate);

    const {
      searchbar: filters,
      quickStats: widgets,
      table: columns,
      kpis,
    } = newData;

    const flatTogglesArr = (arr) => (arr?.data?.length ? arr.data?.map((obj) => obj?.toggles).flat() : []);

    const filtersToggles = flatTogglesArr(filters);
    const widgetsToggles = flatTogglesArr(widgets);
    const columnsToggles = flatTogglesArr(columns);
    const kpisToggles = flatTogglesArr(kpis);

    const editToogleVisibility = (filtersArr, templateRef) => {
      filtersArr.forEach((toggle) => {
        const toggleRef = templateRef?.find((elem) => elem.id === toggle.id);
        if (toggleRef) toggleRef.isVisible = toggle.isVisible;
      });
    };

    editToogleVisibility(filtersToggles, newTemplate?.searchbar?.filters);
    editToogleVisibility(widgetsToggles, newTemplate?.quickStats?.widgets);
    editToogleVisibility(columnsToggles, newTemplate?.table?.columns);
    editToogleVisibility(kpisToggles, newTemplate?.kpis?.kpis);

    setCurrentTemplate(newTemplate);
  };

  const maxFiltersLimit = currentTemplate[SEARCHBAR_SIDEMENU_OPTION]?.maxVisibleLimit;
  const maxWidgetsLimit = currentTemplate[QUICKSTATS_SIDEMENU_OPTION]?.maxVisibleLimit;
  const maxColumnsLimit = currentTemplate[TABLE_SIDEMENU_OPTION]?.maxVisibleLimit;
  const maxKpisLimit = currentTemplate[KPIS_SIDEMENU_OPTION]?.maxVisibleLimit;

  const sideMenuData = {};

  if (fullConfAvailableSearchBarFilters?.length) {
    sideMenuData[SEARCHBAR_SIDEMENU_OPTION] = {
      data: generateSearchbarSideMenu(
        fullConfAvailableSearchBarFilters, currentTemplate?.searchbar?.filters, maxFiltersLimit,
      ),
      maxVisibleLimit: maxFiltersLimit,
    };
  }

  if (fullConfAvailableWidgets?.length) {
    sideMenuData[QUICKSTATS_SIDEMENU_OPTION] = {
      data: generateWidgetsSideMenu(fullConfAvailableWidgets, currentTemplate?.quickStats?.widgets, maxWidgetsLimit),
      maxVisibleLimit: maxWidgetsLimit,
    };
  }

  if (fullConfAvailableTableColumns?.length) {
    sideMenuData[TABLE_SIDEMENU_OPTION] = {
      data: generateTableSideMenu(
        fullConfAvailableTableColumns.filter((obj) => obj.id !== 'checkbox'),
        currentTemplate?.table?.columns,
        maxColumnsLimit,
      ),
      maxVisibleLimit: maxColumnsLimit,
    };
  }

  if (fullConfAvailableKpis?.length) {
    sideMenuData[KPIS_SIDEMENU_OPTION] = {
      data: generateWidgetsSideMenu(fullConfAvailableKpis, currentTemplate?.kpis?.kpis, maxKpisLimit),
      maxVisibleLimit: maxKpisLimit,
    };
  }

  return {
    allAvailableWidgetsProps,
    allAvaillableTableColumnsProps,
    allFullConfSearchBarFilters,
    fullConfAvailableSearchBarFilters,
    fullConfAvailableTableColumns,
    fullConfAvailableWidgets,
    fullConfVisibleKpis,
    fullConfVisibleSearchBarFilters,
    fullConfVisibleTableColumns,
    fullConfVisibleWidgets,
    saveNewTemplateToggles,
    sideMenuData,
    visibleSearchBarFiltersIds,
    visibleTableColumns,
    visibleWidgets,
    visibleWidgetsProps,
  };
}
